import axios from 'axios';
import { API_BASE_URL, getTokenFromStorage, STORAGE_KEYS } from '../constant/constant';

// Create axios instance with default config
const createDiscountApi = () => {
  const discountApi = axios.create({
    baseURL: `${API_BASE_URL}/admin`,
  });

  discountApi.interceptors.request.use(
    (config) => {
      const token = getTokenFromStorage();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  discountApi.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        localStorage.removeItem(STORAGE_KEYS.PERSIST_ROOT);
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );

  return discountApi;
};

const discountApi = createDiscountApi();

export const getDiscountCodeById = async (id) => {
  try {
    const response = await discountApi.get(`/discounts/codes/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || error.response?.data?.msg || error.message || 'Discount code not found');
  }
};

export const getDiscountStats = async () => {
  try {
    const response = await discountApi.get('/discounts/stats');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || error.response?.data?.msg || error.message || 'Failed to load discount stats');
  }
};

export const getDiscountCodes = async (params = {}) => {
  try {
    const response = await discountApi.get('/discounts/codes', { params });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || error.response?.data?.msg || error.message || 'Failed to load discount codes');
  }
};

export const getTopCodesByRevenue = async () => {
  try {
    const response = await discountApi.get('/discounts/top-by-revenue');
    debugger
    const data = Array.isArray(response.data) ? response.data : (response.data?.data || []);
    return data.map((c) => ({
      name: c.name,
      code: c.code,
      status: c.status,
      isDeleted: c.isDeleted,
      value: c.value || c.totalRevenue,
      type: 'revenue',
      startAt: c.startAt || c.startDate,
      endAt: c.endAt || c.expiryDate,
    }));
  } catch (error) {
    throw new Error(error.response?.data?.message || error.response?.data?.msg || error.message || 'Failed to load top performance by revenue');
  }
};

export const getMostUsedCodesByRedemptions = async () => {
  try {
    const response = await discountApi.get('/discounts/most-used-by-redemptions');
    const data = Array.isArray(response.data) ? response.data : (response.data?.data || []);
    return data.map((c) => ({
      name: c.name,
      code: c.code,
      status: c.status,
      isDeleted: c.isDeleted,
      value: c.value || c.totalRedemptions,
      type: 'redemptions',
      startAt: c.startAt || c.startDate,
      endAt: c.endAt || c.expiryDate,
    }));
  } catch (error) {
    throw new Error(error.response?.data?.message || error.response?.data?.msg || error.message || 'Failed to load top performance by redemptions');
  }
};

export const createDiscountCode = async (data) => {
  try {
    const response = await discountApi.post('/discounts/codes', data);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || error.response?.data?.msg || error.message || 'Failed to create discount code');
  }
};

export const updateDiscountCode = async (id, data) => {
  try {
    const response = await discountApi.patch(`/discounts/codes/${id}`, data);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.msg || error.message || 'Failed to update discount code');
  }
};

export const enableDisableDiscountCode = async (id) => {
  try {
    await discountApi.patch(`/discounts/enable-disable-codes/${id}`);
  } catch (error) {
    throw new Error(error.response?.data?.msg || error.message || 'Failed to enable/disable discount code');
  }
};

export const deleteDiscountCode = async (id) => {
  try {
    await discountApi.delete(`/discounts/codes/${id}`);
  } catch (error) {
    throw new Error(error.response?.data?.msg || error.message || 'Failed to delete discount code');
  }
};

export const getDiscountRedemptions = async (id, params = {}) => {
  try {
    const response = await discountApi.get(`/discounts/codes/${id}/redemptions`, { params });
    return response.data;
  } catch (error) {
    console.error("Failed to fetch redemptions:", error);
    return {
      redemptions: [],
      pagination: {
        total: 0,
        page: 1,
        pages: 0
      }
    };
  }
};

export const exportDiscountRedemptions = async (id, params = {}) => {
  try {
    const response = await discountApi.get(`/discounts/codes/${id}/redemptions/export`, {
      params: { ...params, format: 'csv' },
      responseType: 'blob'
    });

    // Create a link to download the file
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;

    // Extract filename from content-disposition if possible
    const contentDisposition = response.headers['content-disposition'];
    let filename = `redemptions-${id}.csv`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }

    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return true;
  } catch (error) {
    console.error("Failed to export redemptions:", error);
    throw new Error(error.response?.data?.msg || error.message || 'Failed to export redemptions');
  }
};
